import { useEffect, useRef, useState, useMemo } from 'react'
import { RenderingEngine, Enums, init as csRenderInit, EVENTS, cache, type Types } from '@cornerstonejs/core'
import * as cornerstoneTools from '@cornerstonejs/tools'
import { init as dicomImageLoaderInit } from '@cornerstonejs/dicom-image-loader'
import axios from 'axios'
import createImageIdsAndCacheMetaData from '../../../utils'
import { SESSION_STORAGE_KEY } from '@common/constants/api'
import { CORNERSTONE_CONFIG, BATCH_SIZE, MAX_CONCURRENT_REQUESTS, BATCH_DELAY_MS } from '../../../constants/phi'
import type { InstanceMetadata, SeriesCache, BatchResult } from '../../../types/phi'
import { StudyModel, InstanceModel } from '@common/config/api/client'
import { getSliderStyles, getGripLinePositions } from '@common/theme'

const {
  PanTool,
  WindowLevelTool,
  StackScrollTool,
  ZoomTool,
  ToolGroupManager,
  Enums: csToolsEnums,
  annotation,
} = cornerstoneTools

const { MouseBindings } = csToolsEnums

/**
 * Gets the latest access token from session storage
 */
function getAccessToken(): string {
  const userData = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!userData) {
    throw new Error('No user data found in Session Storage')
  }

  const userJson = JSON.parse(userData)
  const accessToken = userJson['access_token']

  if (!accessToken) {
    throw new Error('No access token found in session storage')
  }

  return accessToken
}

interface UseImageViewerProps {
  elementRef: React.RefObject<HTMLDivElement>
  selectedSeries: string
  dataToRender: Record<string, StudyModel>
  seriesCache: Record<string, SeriesCache>
  onUpdateSeriesCache: (seriesId: string, cache: SeriesCache) => void
  onUpdateUrlToMetadataMap: (urlToMetadataMap: Record<string, InstanceMetadata>) => void
  onUpdateLoadingProgress: (loaded: number, total: number, loadedImages: number) => void
  onAllImagesLoaded: (isLoaded: boolean) => void
  onCurrentImageChange: (imageId: string | null) => void
  onLoading: (isLoading: boolean) => void
  onForceRedraw: () => void
}

export function useImageViewer({
  elementRef,
  selectedSeries,
  dataToRender,
  seriesCache,
  onUpdateSeriesCache,
  onUpdateUrlToMetadataMap,
  onUpdateLoadingProgress,
  onAllImagesLoaded,
  onCurrentImageChange,
  onLoading,
  onForceRedraw,
}: UseImageViewerProps) {
  // Memoize static slider styles to prevent recreation on every render
  const sliderStyles = useMemo(() => getSliderStyles(), [])
  const gripPositions = useMemo(() => getGripLinePositions(), [])

  const engineRef = useRef<RenderingEngine | null>(null)
  const [engine, setEngine] = useState<RenderingEngine | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const currentLoadingRef = useRef<AbortController | null>(null)

  // Slider state and refs

  const totalImagesRef = useRef<number>(0)
  const sliderContainerRef = useRef<HTMLDivElement | null>(null)
  const sliderThumbRef = useRef<HTMLDivElement | null>(null)
  const isSliderDraggingRef = useRef<boolean>(false)

  // Refs to track active global event listeners for cleanup (prevents memory leaks)

  const mouseMoveHandlerRef = useRef<((e: MouseEvent) => void) | null>(null)
  const mouseUpHandlerRef = useRef<(() => void) | null>(null)
  const touchMoveHandlerRef = useRef<((e: TouchEvent) => void) | null>(null)
  const touchEndHandlerRef = useRef<(() => void) | null>(null)
  const keyboardHandlerRef = useRef<((e: KeyboardEvent) => void) | null>(null)
  const overlayHandlerRef = useRef<(() => void) | null>(null)
  // Initialize Cornerstone
  useEffect(() => {
    const init = async () => {
      try {
        // Initialize Cornerstone Core
        await csRenderInit()
        await cornerstoneTools.init()

        // Add tools
        cornerstoneTools.addTool(PanTool)
        cornerstoneTools.addTool(WindowLevelTool)
        cornerstoneTools.addTool(StackScrollTool)
        cornerstoneTools.addTool(ZoomTool)

        const toolGroup = ToolGroupManager.createToolGroup(CORNERSTONE_CONFIG.toolGroupId)

        toolGroup?.addTool(WindowLevelTool.toolName)
        toolGroup?.addTool(PanTool.toolName)
        toolGroup?.addTool(ZoomTool.toolName)
        toolGroup?.addTool(StackScrollTool.toolName, {
          configuration: {
            deltaY: CORNERSTONE_CONFIG.stackScrollDelta,
          },
        })

        // Configure tool activation and bindings
        toolGroup?.setToolActive(StackScrollTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Primary,
            },
          ],
        })
        toolGroup?.setToolActive(PanTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Auxiliary,
            },
          ],
        })
        toolGroup?.setToolActive(ZoomTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Secondary,
            },
          ],
        })
        toolGroup?.setToolActive(StackScrollTool.toolName, {
          bindings: [
            {
              mouseButton: MouseBindings.Wheel,
            },
          ],
        })

        // Configure annotation style to hide text
        annotation.config.style.setDefaultToolStyles({
          global: {
            color: '#5acce6',
            textBoxFontSize: '0px',
            textBoxColor: 'rgba(0,0,0,0)',
            textBoxBackground: 'rgba(0,0,0,0)',
            textBoxLinkLineWidth: 0,
            textBoxLinkLineDash: '0',
          },
        })

        // Initialize DICOM Image Loader
        dicomImageLoaderInit({
          maxWebWorkers: CORNERSTONE_CONFIG.maxWebWorkers,
        })

        // Configure cache
        cache.setMaxCacheSize(CORNERSTONE_CONFIG.cacheSize)

        const renderingEngine = new RenderingEngine(CORNERSTONE_CONFIG.renderingEngineId)

        const viewportConfig = {
          viewportId: CORNERSTONE_CONFIG.viewportId,
          element: elementRef.current,
          type: Enums.ViewportType.STACK,
          background: [0, 0, 0],
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        renderingEngine.enableElement(viewportConfig)
        engineRef.current = renderingEngine
        setEngine(renderingEngine)
        toolGroup?.addViewport(CORNERSTONE_CONFIG.viewportId, CORNERSTONE_CONFIG.renderingEngineId)

        // Create slider
        createSlider()

        setIsInitialized(true)
      } catch (err) {
        console.error('Error initializing Cornerstone:', err)
      }
    }

    void init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef])

  /**
   * Creating Slider
   * Creates the custom vertical slider UI component
   *
   * @remarks
   * Creates DOM elements for slider container, thumb, and grip lines.
   * Attaches event listeners for click, drag, touch, and keyboard navigation.
   * A separate slider hook can be created later if needed.
   */
  const createSlider = () => {
    if (sliderContainerRef.current) return // Slider already exists

    // Create slider container (track)
    const sliderContainer = document.createElement('div')
    Object.assign(sliderContainer.style, sliderStyles.sliderContainer)

    // Create slider thumb
    const sliderThumb = document.createElement('div')
    Object.assign(sliderThumb.style, sliderStyles.sliderThumb)
    sliderThumb.tabIndex = 0 // Make focusable for keyboard events

    // Add grip lines
    const gripLine1 = document.createElement('div')
    Object.assign(gripLine1.style, sliderStyles.gripLine, { top: gripPositions.first })

    const gripLine2 = document.createElement('div')
    Object.assign(gripLine2.style, sliderStyles.gripLine, { top: gripPositions.second, transform: 'translateY(-50%)' })

    const gripLine3 = document.createElement('div')
    Object.assign(gripLine3.style, sliderStyles.gripLine, { top: gripPositions.third })

    sliderThumb.appendChild(gripLine1)
    sliderThumb.appendChild(gripLine2)
    sliderThumb.appendChild(gripLine3)

    sliderContainer.appendChild(sliderThumb)

    // Event listeners for container (click to move to position)
    sliderContainer.addEventListener('click', handleContainerClick)

    // Event listeners for thumb (drag to move)
    sliderThumb.addEventListener('mousedown', handleThumbMouseDown)
    sliderThumb.addEventListener('touchstart', handleThumbTouchStart)
    //  sliderThumb.addEventListener('keydown', handleKeyboardNavigation)

    elementRef.current?.appendChild(sliderContainer)
    sliderContainerRef.current = sliderContainer
    sliderThumbRef.current = sliderThumb
  }

  /**
   * Handles click on slider track to jump to specific image
   *
   * @param e - Mouse event from track click
   */
  const handleContainerClick = (e: MouseEvent) => {
    if (!sliderContainerRef.current || !sliderThumbRef.current) return
    if (isSliderDraggingRef.current) return

    const rect = sliderContainerRef.current.getBoundingClientRect()
    const clickY = e.clientY - rect.top
    const containerHeight = rect.height
    const thumbHeight = sliderThumbRef.current.offsetHeight

    // Convert click position to image index
    // Top of container = image 0, bottom = last image
    const ratio = Math.max(0, Math.min(clickY / (containerHeight - thumbHeight), 1))
    const imageIndex = Math.min(Math.floor(ratio * totalImagesRef.current), totalImagesRef.current - 1)
    const viewport = engineRef.current?.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
    if (viewport && imageIndex >= 0 && imageIndex < totalImagesRef.current) {
      void viewport.setImageIdIndex(imageIndex).then(() => {
        viewport.render()
        updateOverlay()
      })
    }
  }

  /**
   * Handles mouse down on slider thumb to initiate drag operation
   *
   * @remarks
   * Attaches global mousemove and mouseup listeners to document for smooth dragging.
   * Listeners are stored in refs for cleanup on component unmount.
   *
   * @param e - Mouse event from thumb mousedown
   */
  const handleThumbMouseDown = (e: MouseEvent) => {
    if (!sliderThumbRef.current || !sliderThumbRef.current.contains(e.target as Node)) return
    isSliderDraggingRef.current = true
    e.preventDefault()
    e.stopPropagation() // prevents stackscroll tool from receiving the drag

    const startY = e.clientY
    const startTop = sliderThumbRef.current?.offsetTop || 0

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!sliderContainerRef.current || !sliderThumbRef.current) return

      const deltaY = moveEvent.clientY - startY
      const containerHeight = sliderContainerRef.current.offsetHeight
      const thumbHeight = sliderThumbRef.current.offsetHeight
      const maxTop = containerHeight - thumbHeight

      let newTop = startTop + deltaY
      newTop = Math.max(0, Math.min(newTop, maxTop))

      sliderThumbRef.current.style.top = `${newTop}px`

      // Convert position to image index
      const ratio = maxTop > 0 ? newTop / maxTop : 0
      const imageIndex = Math.min(Math.floor(ratio * totalImagesRef.current), totalImagesRef.current - 1)
      const viewport = engineRef.current?.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
      if (viewport && imageIndex >= 0 && imageIndex < totalImagesRef.current) {
        void viewport.setImageIdIndex(imageIndex).then(() => {
          viewport.render()
          updateOverlay()
        })
      }
    }

    const handleMouseUp = () => {
      isSliderDraggingRef.current = false
      if (mouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', mouseMoveHandlerRef.current)
        mouseMoveHandlerRef.current = null
      }
      if (mouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', mouseUpHandlerRef.current)
        mouseUpHandlerRef.current = null
      }
      updateSliderPosition() // Snap to current position
    }

    mouseMoveHandlerRef.current = handleMouseMove
    mouseUpHandlerRef.current = handleMouseUp
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  /**
   * Handles touch start on slider thumb to initiate touch drag operation
   *
   * @remarks
   * Similar to handleThumbMouseDown but for touch devices.
   * Attaches global touchmove and touchend listeners for smooth dragging.
   *
   * @param e - Touch event from thumb touchstart
   */

  const handleThumbTouchStart = (e: TouchEvent) => {
    if (!sliderThumbRef.current || !sliderThumbRef.current.contains(e.target as Node)) return
    isSliderDraggingRef.current = true

    const startY = e.touches[0].clientY
    const startTop = sliderThumbRef.current?.offsetTop || 0

    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (!sliderContainerRef.current || !sliderThumbRef.current) return

      const deltaY = moveEvent.touches[0].clientY - startY
      const containerHeight = sliderContainerRef.current.offsetHeight
      const thumbHeight = sliderThumbRef.current.offsetHeight
      const maxTop = containerHeight - thumbHeight

      let newTop = startTop + deltaY
      newTop = Math.max(0, Math.min(newTop, maxTop))

      sliderThumbRef.current.style.top = `${newTop}px`

      // Convert position to image index
      const ratio = maxTop > 0 ? newTop / maxTop : 0
      const imageIndex = Math.min(Math.floor(ratio * totalImagesRef.current), totalImagesRef.current - 1)
      const viewport = engineRef.current?.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
      if (viewport && imageIndex >= 0 && imageIndex < totalImagesRef.current) {
        void viewport.setImageIdIndex(imageIndex).then(() => {
          viewport.render()
          updateOverlay()
        })
      }
    }

    const handleTouchEnd = () => {
      isSliderDraggingRef.current = false
      if (touchMoveHandlerRef.current) {
        document.removeEventListener('touchmove', touchMoveHandlerRef.current)
        touchMoveHandlerRef.current = null
      }
      if (touchEndHandlerRef.current) {
        document.removeEventListener('touchend', touchEndHandlerRef.current)
        touchEndHandlerRef.current = null
      }
      updateSliderPosition() // Snap to current position
    }

    touchMoveHandlerRef.current = handleTouchMove
    touchEndHandlerRef.current = handleTouchEnd
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)
  }

  /**
   * Handles keyboard arrow keys for image navigation
   *
   * @remarks
   * ArrowUp: Previous image
   * ArrowDown: Next image
   *
   * @param e - Keyboard event
   */

  const handleKeyboardNavigation = (e: KeyboardEvent) => {
    if (!elementRef.current || !engineRef.current) return

    const viewport = engineRef.current.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
    if (!viewport) return

    let shouldNavigate = false
    let direction = 0 // 1 for next, -1 for previous

    if (e.key === 'ArrowDown') {
      shouldNavigate = true
      direction = 1
      e.preventDefault()
    } else if (e.key === 'ArrowUp') {
      shouldNavigate = true
      direction = -1
      e.preventDefault()
    }

    if (shouldNavigate) {
      const currentIndex = viewport.getCurrentImageIdIndex()
      const total = viewport.getImageIds().length
      const nextIndex = Math.max(0, Math.min(currentIndex + direction, total - 1))

      if (nextIndex === currentIndex) return

      void viewport.setImageIdIndex(nextIndex).then(() => {
        viewport.render()
        updateOverlay()
      })
    }
  }

  /**
   * Updates slider thumb position to match current image index
   *
   * @remarks
   * Skips update during active drag to prevent interference with manual dragging.
   * Called after viewport changes from other navigation methods (keyboard, mouse wheel, etc.)
   */
  const updateSliderPosition = () => {
    if (!sliderContainerRef.current || !sliderThumbRef.current || isSliderDraggingRef.current) return

    const viewport = engineRef.current?.getStackViewport(CORNERSTONE_CONFIG.viewportId)
    if (!viewport) return

    const currentIndex = viewport.getCurrentImageIdIndex()
    const total = viewport.getImageIds().length

    if (total > 0) {
      // Update total images ref
      totalImagesRef.current = total

      // Hide slider when there is only one image — nothing to scroll through
      sliderContainerRef.current.style.display = total === 1 ? 'none' : ''

      if (total === 1) return

      const containerHeight = sliderContainerRef.current.offsetHeight
      const thumbHeight = sliderThumbRef.current.offsetHeight
      const maxTop = containerHeight - thumbHeight

      // Calculate thumb position based on current image index
      const ratio = currentIndex / (total - 1)
      const newTop = ratio * maxTop

      sliderThumbRef.current.style.top = `${newTop}px`
    }
  }

  /**
   * Updates the image index overlay and slider position
   *
   * @remarks
   * Displays "current/total" image count in bottom-right corner.
   * Also triggers slider position update to keep UI in sync.
   */
  const updateOverlay = () => {
    const viewport = engineRef.current?.getStackViewport(CORNERSTONE_CONFIG.viewportId)

    if (!viewport) {
      return
    }

    // Slider sync does not require imageData — update before the guard so
    // rapid keyboard/wheel navigation always keeps the thumb in sync even
    // when Cornerstone transiently returns null for the stack data.
    updateSliderPosition()

    const stack = viewport.getImageData()
    if (!stack) return

    const currentIndex = viewport.getCurrentImageIdIndex() + 1
    const total = viewport.getImageIds().length
    const currentImgId = viewport.getCurrentImageId()

    // Update current image ID
    onCurrentImageChange(currentImgId)

    // Create or update overlay
    let overlayDiv = elementRef.current?.querySelector('.image-index-overlay') as HTMLDivElement

    if (!overlayDiv) {
      overlayDiv = document.createElement('div')
      overlayDiv.className = 'image-index-overlay'
      overlayDiv.style.position = 'absolute'
      overlayDiv.style.bottom = '10px'
      overlayDiv.style.right = '50px'
      overlayDiv.style.color = '#5acce6'
      overlayDiv.style.textShadow = '1px 1px 2px black'
      overlayDiv.style.fontSize = '13px'
      overlayDiv.style.pointerEvents = 'none'
      elementRef.current?.appendChild(overlayDiv)
    }

    overlayDiv.innerHTML = `${currentIndex}/${total}`

    // Update slider position
    updateSliderPosition()
  }

  // Setup viewport for selected series
  useEffect(() => {
    if (!isInitialized || !elementRef.current || !selectedSeries) return

    // Check if dataToRender has the selected series before proceeding
    const hasSelectedSeries = Object.values(dataToRender).some((study) =>
      study.series.some((series) => series.SeriesInstanceUID === selectedSeries),
    )

    if (!hasSelectedSeries) {
      console.log('Selected series not found in dataToRender, waiting...')
      return
    }

    // Capture elementRef.current for cleanup
    const element = elementRef.current

    const setupViewport = async () => {
      // Cancel any previous loading operation
      if (currentLoadingRef.current) {
        console.log('Cancelling previous loading operation')
        currentLoadingRef.current.abort()
        currentLoadingRef.current = null
      }

      // Create a new AbortController for this loading operation
      const abortController = new AbortController()
      currentLoadingRef.current = abortController

      try {
        onLoading(true)
        onUpdateLoadingProgress(0, 0, 0)
        onAllImagesLoaded(false)

        // Recreate slider on new dataset selection
        createSlider()

        const viewport = engine?.getViewport(CORNERSTONE_CONFIG.viewportId) as Types.IStackViewport
        if (!viewport) return

        // Check cache first
        const cachedData = seriesCache[selectedSeries]
        if (cachedData) {
          console.log('Using cached data for series:', selectedSeries)
          onUpdateUrlToMetadataMap(cachedData.urlToMetadataMap)
          viewport.reset(true)

          const totalCachedImages = cachedData.imageIds.length
          totalImagesRef.current = totalCachedImages

          onAllImagesLoaded(true)
          onUpdateLoadingProgress(0, 0, totalCachedImages)
          onLoading(false)

          await viewport.setStack(cachedData.imageIds)
          await viewport.setImageIdIndex(0)

          viewport.resetCamera()
          cornerstoneTools.utilities.stackPrefetch.enable(viewport.element)
          viewport.render()

          // Add event listeners — use refs so removals always match the registered reference
          if (overlayHandlerRef.current) {
            elementRef.current?.removeEventListener(EVENTS.IMAGE_RENDERED, overlayHandlerRef.current)
            elementRef.current?.removeEventListener(EVENTS.STACK_VIEWPORT_SCROLL, overlayHandlerRef.current)
          }
          overlayHandlerRef.current = updateOverlay
          elementRef.current?.addEventListener(EVENTS.IMAGE_RENDERED, updateOverlay)
          elementRef.current?.addEventListener(EVENTS.STACK_VIEWPORT_SCROLL, updateOverlay)
          if (keyboardHandlerRef.current) elementRef.current?.removeEventListener('keydown', keyboardHandlerRef.current)
          keyboardHandlerRef.current = handleKeyboardNavigation
          elementRef.current?.addEventListener('keydown', handleKeyboardNavigation)
          elementRef.current?.focus()

          updateOverlay()
          onForceRedraw()
          return
        }

        // Fetch data if not cached
        console.log('Fetching data for series:', selectedSeries)

        // Find the series to process
        const studyKey = Object.keys(dataToRender).find((studyUID) =>
          dataToRender[studyUID]?.series.some((s) => s.SeriesInstanceUID === selectedSeries),
        )

        if (!studyKey) {
          console.error('Selected series not found')
          onLoading(false)
          return
        }

        const seriesToProcess = dataToRender[studyKey].series.find((x) => x.SeriesInstanceUID === selectedSeries)

        if (!seriesToProcess) {
          console.error('Selected series not found')
          onLoading(false)
          return
        }

        const totalInstances = seriesToProcess.instances.length
        onUpdateLoadingProgress(0, totalInstances, 0)

        const countedUrls = new Set<string>()
        const fetchedImageIds: string[] = []
        const fetchedUrlToMetadataMap: Record<string, InstanceMetadata> = {}

        let viewportInitialized = false

        // Add changes to sort image loading sequence by InstanceNumber to ensure images are loaded in correct order in viewport
        // SORT INSTANCES BY INSTANCE NUMBER BEFORE BATCHING
        const sortedInstances = [...seriesToProcess.instances].sort((a, b) => {
          const aNum = Number(a.metadata?.InstanceNumber) || 0
          const bNum = Number(b.metadata?.InstanceNumber) || 0
          return aNum - bNum
        })

        // Process instances in batches
        const processBatch = async (batchInstances: InstanceModel[], batchStart: number, batchEnd: number) => {
          const batch_access_token = getAccessToken()
          console.log(`Processing batch ${batchStart} to ${batchEnd} of ${totalInstances}`)

          const activeRequests = new Set<Promise<BatchResult>>()
          const batchResults: Promise<BatchResult>[] = []
          let batchLoadedCount = 0

          for (const instance of batchInstances) {
            const instanceUrl = instance.url.replace(/^dicomweb:/, '')
            const instanceMetadata = {
              ...instance.metadata,
              SOPInstanceUID: instance.metadata?.SOPInstanceUID || '',
            } as InstanceMetadata
            const instanceNumber = instanceMetadata.InstanceNumber
              ? parseInt(String(instanceMetadata.InstanceNumber), 10)
              : 0

            const trackingKey = `${instanceUrl}:${instanceMetadata.SOPInstanceUID}`

            // Wait if we've hit the concurrency limit
            while (activeRequests.size >= MAX_CONCURRENT_REQUESTS) {
              await Promise.race(activeRequests)
            }

            const requestPromise = axios
              .get(instanceUrl, {
                headers: {
                  authorization: `Bearer ${batch_access_token}`,
                },
                signal: abortController.signal,
              })
              .then((response) => {
                if (!countedUrls.has(trackingKey)) {
                  countedUrls.add(trackingKey)
                  batchLoadedCount++
                  console.log(`New image: ${trackingKey}`)
                }
                return {
                  data: response.data,
                  instanceMetadata,
                  instanceNumber,
                }
              })
              .finally(() => {
                activeRequests.delete(requestPromise)
              })

            activeRequests.add(requestPromise)
            batchResults.push(requestPromise)
          }

          // Wait for all requests in this batch to complete
          const results = await Promise.all(batchResults)

          // Sort instances by InstanceNumber within the batch
          results.sort((a, b) => a.instanceNumber - b.instanceNumber)

          // Update progress
          if (batchLoadedCount > 0) {
            const nextCount = Math.min(countedUrls.size, totalInstances)
            onUpdateLoadingProgress(nextCount, totalInstances, fetchedImageIds.length)
          }

          return results
        }

        for (let batchStart = 0; batchStart < totalInstances; batchStart += BATCH_SIZE) {
          if (abortController.signal.aborted) {
            console.log('Loading operation was aborted - exiting batch processing')
            throw new Error('Loading aborted')
          }

          const batchEnd = Math.min(batchStart + BATCH_SIZE, totalInstances)

          // Slice from sortedInstances instead of seriesToProcess.instances to maintain InstanceNumber ordering
          // This ensures images are loaded in the correct sequence for viewport display
          const batchInstances = sortedInstances.slice(batchStart, batchEnd)

          try {
            const batchResults = await processBatch(batchInstances, batchStart, batchEnd)

            // Process each instance in the batch
            for (const instanceData of batchResults) {
              const instanceImageIds = createImageIdsAndCacheMetaData(
                instanceData.data.url,
                instanceData.instanceMetadata,
              )
              fetchedImageIds.push(...instanceImageIds)
              fetchedUrlToMetadataMap[instanceData.data.url] = instanceData.instanceMetadata
            }

            // Update loaded image count
            onUpdateLoadingProgress(countedUrls.size, totalInstances, fetchedImageIds.length)

            // Initialize viewport with first batch
            if (!viewportInitialized && batchStart === 0 && fetchedImageIds.length > 0) {
              console.log(`Initializing viewport with first batch: ${fetchedImageIds.length} images`)
              totalImagesRef.current = fetchedImageIds.length

              viewport.reset(true)
              await viewport.setStack(fetchedImageIds)
              await viewport.setImageIdIndex(0)

              viewport.resetCamera()
              onLoading(false)

              // Add event listeners — use refs so removals always match the registered reference
              if (overlayHandlerRef.current) {
                elementRef.current?.removeEventListener(EVENTS.IMAGE_RENDERED, overlayHandlerRef.current)
                elementRef.current?.removeEventListener(EVENTS.STACK_VIEWPORT_SCROLL, overlayHandlerRef.current)
              }
              overlayHandlerRef.current = updateOverlay
              elementRef.current?.addEventListener(EVENTS.IMAGE_RENDERED, updateOverlay)
              elementRef.current?.addEventListener(EVENTS.STACK_VIEWPORT_SCROLL, updateOverlay)
              if (keyboardHandlerRef.current)
                elementRef.current?.removeEventListener('keydown', keyboardHandlerRef.current)
              keyboardHandlerRef.current = handleKeyboardNavigation
              elementRef.current?.addEventListener('keydown', handleKeyboardNavigation)
              elementRef.current?.focus()

              updateOverlay()
              cornerstoneTools.utilities.stackPrefetch.enable(viewport.element)
              viewportInitialized = true

              // If this is the only batch, mark as complete
              if (batchEnd === totalInstances) {
                onAllImagesLoaded(true)
              }
            }

            // Add delay between batches
            if (batchEnd < totalInstances) {
              await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS))
            }
          } catch (error) {
            if (abortController.signal.aborted) {
              console.log('Batch processing was aborted')
              throw new Error('Loading aborted')
            }
            throw error
          }
        }

        // Check if this operation was aborted before final setup
        if (abortController.signal.aborted) {
          console.log('Loading operation was aborted before final setup')
          throw new Error('Loading aborted')
        }

        // Cache the fetched data
        onUpdateSeriesCache(selectedSeries, {
          imageIds: fetchedImageIds,
          urlToMetadataMap: fetchedUrlToMetadataMap,
        })
        console.log('Cached data for series:', selectedSeries)

        // Update the stack if viewport was already initialized
        if (viewportInitialized) {
          console.log(`Updating stack with all images: ${fetchedImageIds.length}`)
          totalImagesRef.current = fetchedImageIds.length

          const currentIndex = viewport.getCurrentImageIdIndex()
          await viewport.setStack(fetchedImageIds)

          if (currentIndex >= 0 && currentIndex < fetchedImageIds.length) {
            await viewport.setImageIdIndex(currentIndex)
          }

          viewport.resetCamera()
          cornerstoneTools.utilities.stackPrefetch.enable(viewport.element)
          viewport.render()
        }

        // Mark all images as loaded
        onAllImagesLoaded(true)
        onUpdateLoadingProgress(0, 0, fetchedImageIds.length)
        onUpdateUrlToMetadataMap(fetchedUrlToMetadataMap)
        onForceRedraw()
      } catch (err) {
        if (err instanceof Error && err.message === 'Loading aborted') {
          console.log('Loading operation was cancelled')
        } else {
          console.error('Error setting up viewport:', err)
        }
        onLoading(false)
        onUpdateLoadingProgress(0, 0, 0)
      } finally {
        if (currentLoadingRef.current === abortController) {
          currentLoadingRef.current = null
        }
      }
    }

    void setupViewport()

    // Cleanup function
    return () => {
      console.log('Component unmounted or series changed, cleaning up...')
      if (currentLoadingRef.current) {
        currentLoadingRef.current.abort()
        currentLoadingRef.current = null
      }
      onUpdateLoadingProgress(0, 0, 0)

      // Clean up slider event listeners
      if (sliderContainerRef.current) {
        sliderContainerRef.current.removeEventListener('click', handleContainerClick)
      }
      if (sliderThumbRef.current) {
        sliderThumbRef.current.removeEventListener('mousedown', handleThumbMouseDown)
        sliderThumbRef.current.removeEventListener('touchstart', handleThumbTouchStart)
      }
      if (mouseMoveHandlerRef.current) {
        document.removeEventListener('mousemove', mouseMoveHandlerRef.current)
        mouseMoveHandlerRef.current = null
      }
      if (mouseUpHandlerRef.current) {
        document.removeEventListener('mouseup', mouseUpHandlerRef.current)
        mouseUpHandlerRef.current = null
      }
      if (touchMoveHandlerRef.current) {
        document.removeEventListener('touchmove', touchMoveHandlerRef.current)
        touchMoveHandlerRef.current = null
      }
      if (touchEndHandlerRef.current) {
        document.removeEventListener('touchend', touchEndHandlerRef.current)
        touchEndHandlerRef.current = null
      }
      if (overlayHandlerRef.current) {
        element.removeEventListener(EVENTS.IMAGE_RENDERED, overlayHandlerRef.current)
        element.removeEventListener(EVENTS.STACK_VIEWPORT_SCROLL, overlayHandlerRef.current)
        overlayHandlerRef.current = null
      }
      if (keyboardHandlerRef.current) {
        element.removeEventListener('keydown', keyboardHandlerRef.current)
        keyboardHandlerRef.current = null
      }

      // Remove slider from DOM
      if (sliderContainerRef.current && element) {
        element.removeChild(sliderContainerRef.current)
        sliderContainerRef.current = null
        sliderThumbRef.current = null
      }

      // Remove image-index overlay — not cleaned up by React since it is
      // created imperatively; leaving it causes stale count text between series changes.
      const existingOverlay = element.querySelector('.image-index-overlay')
      if (existingOverlay) {
        element.removeChild(existingOverlay)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, selectedSeries, engine])

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      if (currentLoadingRef.current) {
        currentLoadingRef.current.abort()
        currentLoadingRef.current = null
      }
    }
  }, [])

  return {
    engine,
    isInitialized,
    updateOverlay,
    updateSliderPosition,
  }
}
